import { applicationService } from "@/app/services/ApplicationService";
import applicationStatus from "@/models/applicationStatus";
import attendanceStatus from "@/models/attendanceStatus";
import store from "@/store";
import { Application } from "@/types/order/Application";
import {
  isSameDay,
  now,
  parseISO,
  startOfToday,
  startOfTomorrow
} from "@/utils/date";
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";

// localStorage に保存されたデータを利用する
const ls = localStorage.getItem("vuex");
const applicationLS = ls !== null ? JSON.parse(ls).application : undefined;

@Module({
  dynamic: true,
  namespaced: true,
  store,
  name: "application",
  preserveState: applicationLS !== undefined
})
class ApplicationModule extends VuexModule {
  _applications: Application[] = [];

  // 応募データ取得中かどうか
  isFetchingApplications?: boolean = false;

  get applications() {
    return this._applications || [];
  }

  // 勤務中
  get doingApplication() {
    return this.applications.find(
      application => application.status === applicationStatus.DOING
    );
  }

  // 勤務予定
  get appliedApplications() {
    return this.applications.filter(
      application => application.status === applicationStatus.APPLIED
    );
  }

  // 明日の勤務予定
  get tomorrowAppliedApplication() {
    // yyyy-mm-dd 形式で比較する
    const tomorrow = startOfTomorrow();
    return this.appliedApplications.find(application => {
      if (application.isConfirmedReminder) {
        return false;
      }
      const orderStartDate = parseISO(application.order.date);
      return isSameDay(tomorrow, orderStartDate);
    });
  }

  // 未出勤
  get notAttendingApplication() {
    return this.applications.find(
      application =>
        application.status === applicationStatus.APPLIED &&
        now() > parseISO(application.appliedStartWorkAt)
    );
  }

  // 未退勤
  get notLeavingApplication() {
    return this.applications.find(
      application =>
        application.status === applicationStatus.DOING &&
        now() > parseISO(application.appliedFinishWorkAt)
    );
  }

  // 勤務完了
  get doneApplications() {
    return this.applications.filter(
      application => application.status === applicationStatus.DONE
    );
  }

  // 未評価
  get notEvaluatedApplications() {
    return this.doneApplications.filter(
      application =>
        !application.attendanceCorrection ||
        application.attendanceCorrection.status === attendanceStatus.APPROVED
    );
  }

  // 勤怠未承認
  get attendanceCorrections() {
    return this.doneApplications.filter(
      application =>
        application.attendanceCorrection &&
        application.attendanceCorrection.status !== attendanceStatus.APPROVED
    );
  }

  // 仕事管理フッター表示件数
  get notificationApplicationCount() {
    return this.attendanceApplicationCount + this.notEvaluatedApplicationCount;
  }

  // 勤怠タブ表示件数
  get attendanceApplicationCount() {
    return (
      // notLeavingApplication がある時は doingApplication も同時に存在するが、カウントとしては1件とする
      (this.doingApplication || this.notLeavingApplication ? 1 : 0) +
      (this.notAttendingApplication ? 1 : 0) +
      this.attendanceCorrections.length
    );
  }

  // 未評価タブ表示件数
  get notEvaluatedApplicationCount() {
    return this.notEvaluatedApplications.length;
  }

  // 直近の勤務前の仕事
  get mostRecentAppliedApplication() {
    // sort は in-place で処理されるため、slice() でコピーを作成する
    const appliedApplications = this.appliedApplications.slice();
    const today = startOfToday();
    const nowTime = now();
    return appliedApplications
      .sort(
        (a, b) =>
          parseISO(a.appliedStartWorkAt) - parseISO(b.appliedStartWorkAt)
      )
      .find(application => {
        // 当日の応募があればそれを対象とする
        if (isSameDay(today, parseISO(application.order.date))) {
          return true;
        }
        // そうでない場合、現在日時以降の応募を対象とする
        return nowTime < parseISO(application.appliedStartWorkAt);
      });
  }

  // 勤務開始日時を過ぎて出勤してない案件一覧を返却する
  get notAttendedAndCorrectedApplications() {
    return this.applications.filter(
      application =>
        application.status === applicationStatus.APPLIED &&
        now() > parseISO(application.appliedStartWorkAt) &&
        application.attendanceCorrection === null
    );
  }

  // 勤務終了日時を過ぎて退勤してない案件一覧を返却する
  get notLeavingWorkApplications() {
    return this.applications.filter(
      application =>
        application.status === applicationStatus.DOING &&
        now() > parseISO(application.appliedFinishWorkAt)
    );
  }

  @Mutation
  private SET_IS_FETCHING_APPLICATIONS(value: boolean) {
    this.isFetchingApplications = value;
  }

  @Action
  resetIsFetchingApplications() {
    this.SET_IS_FETCHING_APPLICATIONS(false);
  }

  @Mutation
  private SET_APPLICATIONS(applications: Application[]) {
    this._applications = applications;
  }

  @Action
  async fetchApplications() {
    try {
      this.SET_IS_FETCHING_APPLICATIONS(true);
      const { results } = await applicationService.fetchApplications({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        status__in: `${applicationStatus.APPLIED},${applicationStatus.DOING},${applicationStatus.DONE},${applicationStatus.REQESTED_CANCEL}`,
        limit: 9999,
        ordering: "applied_start_work_at"
      });
      this.SET_APPLICATIONS(results);
    } finally {
      this.SET_IS_FETCHING_APPLICATIONS(false);
    }
  }
}

export const applicationStore = getModule(ApplicationModule);
